import type { Transaction } from '../types';
import { costAsPercent } from '../components/pages/agents/agentCdnUtils';

export type BalanceToCreditsConversionDisplay = {
  buyerType: string;
  buyerRoleLabel: string;
  usdAmount: number;
  creditAmount: number;
  costRate: number;
  costRatePercentLabel: string;
  formulaLine: string;
};

function buyerRoleLabel(buyerType: string): string {
  const t = buyerType.toLowerCase();
  if (t === 'shop') return 'Shop';
  if (t === 'agent') return 'Agent';
  if (t === 'super_agent') return 'Super agent';
  return buyerType.replace(/_/g, ' ');
}

function isBalanceToCreditsTransaction(t: Transaction): boolean {
  const meta = t.metadata || {};
  const sync = (t.syncMetadata || {}) as Record<string, unknown>;
  if (meta.balanceToCredits === true || sync.balanceToCredits === true) return true;
  if (t.provider === 'balance_conversion') return true;
  const tid = String(t.transactionId || '');
  return tid.startsWith('BALANCE_TO_CREDITS_');
}

/** USD → credits conversion context for admin transaction detail (shop/agent/super_agent buyer). */
export function getBalanceToCreditsConversionDisplay(
  t: Transaction,
): BalanceToCreditsConversionDisplay | null {
  if (!isBalanceToCreditsTransaction(t)) return null;

  const buyerType = String(t.buyerType || '').trim();
  if (!buyerType || buyerType === 'user' || buyerType === 'player') return null;

  const fs = t.fundingSummary;
  const meta = t.metadata || {};

  const usdAmount = Number(fs?.usdAmount ?? meta.usdAmount ?? t.amount);
  const creditAmount = Number(fs?.creditAmount ?? meta.creditAmount);
  const costRate = Number(fs?.costRateApplied ?? meta.costRateApplied);

  if (!Number.isFinite(usdAmount) || usdAmount <= 0) return null;
  if (!Number.isFinite(creditAmount) || creditAmount <= 0) return null;
  if (!Number.isFinite(costRate) || costRate <= 0) return null;

  const role = buyerRoleLabel(buyerType);
  const creditsFormatted = creditAmount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  const usdFormatted = usdAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const rateFormatted = costRate.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

  return {
    buyerType,
    buyerRoleLabel: role,
    usdAmount,
    creditAmount,
    costRate,
    costRatePercentLabel: costAsPercent({ costRate }),
    formulaLine: `${creditsFormatted} credits = $${usdFormatted} ÷ ${rateFormatted} (${role} cost rate)`,
  };
}
